import { Box, Divider, HStack, Image, Text, VStack } from '@chakra-ui/react';
import { Clock } from 'lucide-react';
import Markdown from '@/components/atoms/Markdown/Markdown';
import AuthorHeader from '@/modules/content/feed/reader/parts/AuthorHeader';
import ChapterHeading from '@/modules/content/feed/reader/parts/ChapterHeading';
import SectionImage from '@/modules/content/feed/reader/parts/SectionImage';
import TitleBlock from '@/modules/content/feed/reader/parts/TitleBlock';
import {
  splitArticleSections,
  scrollToChapter,
  TReaderDetails,
} from '@/modules/content/feed/reader/model/articleStructure';
import { readingTime, sectionWords } from '@/modules/content/feed/reader/model/readingTime';
import { toRoman } from '@/modules/content/feed/reader/model/romanNumerals';
import { readerCardProps } from '@/modules/content/feed/reader/model/readerStyles';
import { TContent } from '@/types/Content.types';

const ArticleBody = ({ content, details }: { content: TContent; details: TReaderDetails }) => {
  const { lead, chapters, fullDescription } = splitArticleSections(content, details);

  return (
    <>
      {/* Header card: author + title + full description + section index */}
      <VStack {...readerCardProps} align="stretch" gap="22px">
        <AuthorHeader content={content} />
        <TitleBlock content={content} />

        {fullDescription && (
          <VStack align="stretch" gap="10px">
            <Text fontSize="16px" fontWeight={800} color="text.darkBlue">
              Full Description
            </Text>
            <Markdown>{fullDescription}</Markdown>
          </VStack>
        )}

        {lead?.imageUrl && <SectionImage src={lead.imageUrl} alt={content.title || 'Article image'} />}

        {chapters.length > 0 && (
          <VStack align="stretch" gap="10px">
            <Text fontSize="16px" fontWeight={800} color="text.darkBlue">
              Article Sections
            </Text>
            <VStack align="stretch" gap="10px">
              {chapters.map((chapter, index) => (
                <HStack
                  key={chapter._id ?? index}
                  as="button"
                  onClick={() => scrollToChapter(index)}
                  justify="space-between"
                  gap="14px"
                  padding="14px 18px"
                  borderRadius="14px"
                  backgroundColor="background.lightGray"
                  border="1px solid"
                  borderColor="background.separatorBlue"
                  transition="all 0.15s ease"
                  _hover={{ borderColor: 'primary.300', transform: 'translateY(-1px)' }}
                  textAlign="left"
                >
                  <HStack gap="14px" minWidth={0}>
                    <Box
                      flexShrink={0}
                      width="28px"
                      height="28px"
                      borderRadius="8px"
                      backgroundColor="primary.500"
                      color="white"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      fontSize="13px"
                      fontWeight={800}
                    >
                      {index + 1}
                    </Box>
                    <Text fontSize="14px" fontWeight={700} color="text.darkBlue" noOfLines={1}>
                      {chapter.title || `Section ${index + 1}`}
                    </Text>
                  </HStack>
                  <HStack gap="6px" color="text.mediumGray" flexShrink={0}>
                    <Clock size={14} />
                    <Text fontSize="12px" whiteSpace="nowrap">
                      {readingTime(sectionWords(chapter))}
                    </Text>
                  </HStack>
                </HStack>
              ))}
            </VStack>
          </VStack>
        )}
      </VStack>

      {/* Cover hero between the index and the chapters */}
      {content.effectiveThumbnailUrl && (
        <Image
          src={content.effectiveThumbnailUrl}
          alt={content.title || 'Cover'}
          width="100%"
          height={{ base: '220px', md: '320px' }}
          objectFit="cover"
          borderRadius="20px"
        />
      )}

      {/* Chapters */}
      {chapters.map((chapter, index) => (
        <VStack key={chapter._id ?? index} {...readerCardProps} id={`chapter-${index}`} align="stretch" gap="18px" scrollMarginTop="20px">
          <ChapterHeading>
            Chapter {toRoman(index + 1)}: {chapter.title || `Section ${index + 1}`}
          </ChapterHeading>
          <Divider />
          {chapter.text && <Markdown>{chapter.text}</Markdown>}
          {chapter.imageUrl && <SectionImage src={chapter.imageUrl} alt={chapter.title || `Chapter ${index + 1}`} />}

          {(chapter.subsections ?? []).map((sub, subIndex) => (
            <VStack key={sub._id ?? subIndex} align="stretch" gap="10px">
              {sub.title && (
                <Text fontSize="18px" fontWeight={700} color="text.mediumBlue">
                  {sub.title}
                </Text>
              )}
              {sub.text && <Markdown>{sub.text}</Markdown>}
              {sub.imageUrl && <SectionImage src={sub.imageUrl} alt={sub.title || 'Subsection'} />}
            </VStack>
          ))}
        </VStack>
      ))}
    </>
  );
};

export default ArticleBody;
